import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip,
    CircularProgress,
    Divider,
    useTheme,
    useMediaQuery,
    Pagination,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    IconArrowLeft,
    IconCamera,
    IconCheck,
    IconClock,
    IconX,
    IconZoomIn,
    IconZoomOut,
    IconRefresh,
    IconMapPin,
    IconVideo,
    IconAlertCircle,
    IconLeaf,
    IconPhoto,
    IconNotes,
    IconSeeding,
    IconTractor,
    IconPlant,
    IconRecycle,
} from '@tabler/icons-react';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';

// ===== PROOF TYPE META =====
const PROOF_TYPE_META = {
    'Initial Setup':       { icon: <IconSeeding size={16} stroke={1.8} />,  color: '#0284C7', bg: '#E0F2FE' },
    'Farm Activity':       { icon: <IconTractor size={16} stroke={1.8} />,  color: '#7C3AED', bg: '#EDE9FE' },
    'Plantation/Crop':     { icon: <IconPlant   size={16} stroke={1.8} />,  color: '#059669', bg: '#D1FAE5' },
    'Residue Management':  { icon: <IconRecycle size={16} stroke={1.8} />,  color: '#D97706', bg: '#FEF3C7' },
};

// ===== STATUS META =====
const STATUS_META = {
    Pending:  { label: 'Pending',  icon: <IconClock    size={13} />, color: '#B45309', bg: '#FEF3C7' },
    Approved: { label: 'Approved', icon: <IconCheck    size={13} />, color: '#065F46', bg: '#D1FAE5' },
    Rejected: { label: 'Rejected', icon: <IconAlertCircle size={13} />, color: '#991B1B', bg: '#FEE2E2' },
};

// ===== TRANSLATIONS =====
const t = {
    en: {
        title: 'My Submitted Proofs',
        subtitle: 'Track all your uploaded carbon credit evidence',
        back: 'Back',
        noProofs: 'No proofs submitted yet',
        noProofsHint: 'Upload your first proof to start the verification process',
        uploadNow: 'Upload Proof',
        project: 'Project',
        type: 'Type',
        date: 'Submitted',
        status: 'Status',
        photos: 'Photos',
        videoUrl: 'Video Link',
        gps: 'GPS Location',
        notes: 'Additional Notes',
        proofDetail: 'Proof Details',
        noPhotos: 'No photos attached',
        viewPhoto: 'View',
    },
    hi: {
        title: 'मेरे सबमिट किए गए प्रमाण',
        subtitle: 'अपने सभी अपलोड किए गए कार्बन क्रेडिट प्रमाण ट्रैक करें',
        back: 'वापस',
        noProofs: 'अभी तक कोई प्रमाण सबमिट नहीं किया',
        noProofsHint: 'सत्यापन प्रक्रिया शुरू करने के लिए अपना पहला प्रमाण अपलोड करें',
        uploadNow: 'प्रमाण अपलोड करें',
        project: 'प्रोजेक्ट',
        type: 'प्रकार',
        date: 'सबमिट किया',
        status: 'स्थिति',
        photos: 'तस्वीरें',
        videoUrl: 'वीडियो लिंक',
        gps: 'GPS स्थान',
        notes: 'अतिरिक्त टिप्पणियाँ',
        proofDetail: 'प्रमाण विवरण',
        noPhotos: 'कोई तस्वीर संलग्न नहीं',
        viewPhoto: 'देखें',
    },
    mr: {
        title: 'माझे सबमिट केलेले पुरावे',
        subtitle: 'तुमचे सर्व अपलोड केलेले कार्बन क्रेडिट पुरावे ट्रॅक करा',
        back: 'मागे',
        noProofs: 'अद्याप कोणताही पुरावा सबमिट केलेला नाही',
        noProofsHint: 'पडताळणी प्रक्रिया सुरू करण्यासाठी तुमचा पहिला पुरावा अपलोड करा',
        uploadNow: 'पुरावा अपलोड करा',
        project: 'प्रकल्प',
        type: 'प्रकार',
        date: 'सबमिट केले',
        status: 'स्थिती',
        photos: 'फोटो',
        videoUrl: 'व्हिडिओ लिंक',
        gps: 'GPS ठिकाण',
        notes: 'अतिरिक्त नोंदी',
        proofDetail: 'पुरावा तपशील',
        noPhotos: 'कोणताही फोटो नाही',
        viewPhoto: 'पहा',
    },
};

// ===== FORMAT DATE =====
const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ===== STATUS BADGE =====
const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META['Pending'];
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            px: '10px',
            py: '4px',
            borderRadius: '20px',
            background: meta.bg,
            color: meta.color,
            fontSize: '12px',
            fontWeight: 700,
            fontFamily: fonts.publicSans || fonts.poppins,
        }}>
            {meta.icon}
            {meta.label}
        </Box>
    );
};

// ===== PROOF CARD =====
const ProofCard = ({ proof, tLocal, onClick }) => {
    const typeMeta = PROOF_TYPE_META[proof.proofType] || PROOF_TYPE_META['Initial Setup'];
    const firstPhoto = proof.photos?.[0];
    const photoCount = proof.photos?.length || 0;

    return (
        <Box
            onClick={() => onClick(proof)}
            sx={{
                display: 'flex',
                gap: '16px',
                p: '16px',
                borderRadius: '16px',
                border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                background: '#FFFFFF',
                boxShadow: '0px 1px 4px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.09)',
                    transform: 'translateY(-2px)',
                    borderColor: colors.carbon_bg_main || '#00B4D8',
                },
            }}
        >
            {/* Thumbnail */}
            <Box sx={{
                width: '72px',
                height: '72px',
                borderRadius: '12px',
                overflow: 'hidden',
                flexShrink: 0,
                background: '#F1F5F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
            }}>
                {firstPhoto ? (
                    <>
                        <img
                            src={`${url}/${firstPhoto}`}
                            alt="proof"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {photoCount > 1 && (
                            <Box sx={{
                                position: 'absolute', bottom: '2px', right: '2px',
                                background: 'rgba(0,0,0,0.55)', color: '#fff',
                                borderRadius: '6px', px: '5px', py: '1px',
                                fontSize: '10px', fontWeight: 700,
                            }}>
                                +{photoCount - 1}
                            </Box>
                        )}
                    </>
                ) : (
                    <IconPhoto size={28} color="#CBD5E1" stroke={1.5} />
                )}
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Project name */}
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 700,
                    fontSize: '14px',
                    color: colors.carbon_text_main || '#0F1729',
                    mb: '2px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {proof.projectName || 'Carbon Project'}
                </Typography>

                {/* Location */}
                {proof.projectLocation && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mb: '8px' }}>
                        <IconMapPin size={12} color="#94A3B8" />
                        <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontFamily: fonts.publicSans || fonts.poppins }}>
                            {proof.projectLocation}
                        </Typography>
                    </Box>
                )}

                {/* Type pill + Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        px: '8px', py: '3px', borderRadius: '20px',
                        background: typeMeta.bg, color: typeMeta.color,
                        fontSize: '11px', fontWeight: 700,
                        fontFamily: fonts.publicSans || fonts.poppins,
                    }}>
                        {typeMeta.icon}
                        {proof.proofType}
                    </Box>
                    <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontFamily: fonts.publicSans || fonts.poppins }}>
                        {formatDate(proof.createdAt)}
                    </Typography>
                </Box>
            </Box>

            {/* Status */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', flexShrink: 0 }}>
                <StatusBadge status={proof.status} />
                {photoCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mt: '8px' }}>
                        <IconCamera size={13} color="#94A3B8" />
                        <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>{photoCount}</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ===== SECTION LABEL helper =====
const SectionLabel = ({ icon, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '10px' }}>
        {React.cloneElement(icon, { size: 15, color: '#94A3B8' })}
        <Typography sx={{
            fontSize: '10px', fontWeight: 700, color: '#94A3B8',
            textTransform: 'uppercase', letterSpacing: '0.7px',
            fontFamily: fonts.publicSans || fonts.poppins,
        }}>
            {label}
        </Typography>
    </Box>
);

// ===== DETAIL MODAL =====
const ProofDetailModal = ({ proof, open, onClose, tLocal, onImageClick }) => {
    if (!proof) return null;
    const typeMeta = PROOF_TYPE_META[proof.proofType] || PROOF_TYPE_META['Initial Setup'];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
            {/* ---- HEADER ---- */}
            <DialogTitle sx={{
                px: '28px', pt: '22px', pb: '18px',
                borderBottom: `1px solid #F1F5F9`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontFamily: fonts.manrope || fonts.poppins,
                fontWeight: 700, fontSize: '18px',
                color: colors.carbon_text_main || '#0F1729',
            }}>
                {tLocal.proofDetail}
                <IconButton onClick={onClose} sx={{ color: '#94A3B8', p: '6px', '&:hover': { color: '#0F1729', background: '#F1F5F9' } }}>
                    <IconX size={20} />
                </IconButton>
            </DialogTitle>

            {/* ---- SCROLLABLE BODY ---- */}
            <DialogContent sx={{ p: '0 !important', maxHeight: '78vh', overflowY: 'auto' }}>

                {/* Top summary bar: status + type badges */}
                <Box sx={{ px: '28px', py: '18px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #F8FAFC', background: '#FAFBFC' }}>
                    <StatusBadge status={proof.status} />
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        px: '10px', py: '4px', borderRadius: '20px',
                        background: typeMeta.bg, color: typeMeta.color,
                        fontSize: '12px', fontWeight: 700,
                        fontFamily: fonts.publicSans || fonts.poppins,
                    }}>
                        {typeMeta.icon}
                        {proof.proofType}
                    </Box>
                </Box>

                {/* Project + Date row */}
                <Box sx={{ px: '28px', py: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', borderBottom: '1px solid #F1F5F9' }}>
                    <Box>
                        <SectionLabel icon={<IconLeaf />} label={tLocal.project} />
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: colors.carbon_text_main || '#0F1729', lineHeight: 1.4 }}>
                            {proof.projectName || 'Carbon Project'}
                        </Typography>
                        {proof.projectLocation && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '4px' }}>
                                <IconMapPin size={12} color="#94A3B8" />
                                <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontFamily: fonts.publicSans || fonts.poppins }}>
                                    {proof.projectLocation}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box>
                        <SectionLabel icon={<IconClock />} label={tLocal.date} />
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: colors.carbon_text_main || '#0F1729' }}>
                            {formatDate(proof.createdAt)}
                        </Typography>
                    </Box>
                </Box>

                {/* Photos section */}
                <Box sx={{ px: '28px', pt: '22px', pb: '22px', borderBottom: '1px solid #F1F5F9' }}>
                    <SectionLabel icon={<IconCamera />} label={tLocal.photos} />
                    {proof.photos?.length > 0 ? (
                        <Grid container spacing={1.5}>
                            {proof.photos.map((photo, i) => (
                                <Grid item xs={4} key={i}>
                                    <Box
                                        onClick={() => onImageClick(`${url}/${photo}`)}
                                        sx={{
                                            display: 'block',
                                            aspectRatio: '1/1',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            border: `1px solid #E2E8F0`,
                                            position: 'relative',
                                            cursor: 'pointer',
                                            '&:hover .overlay': { opacity: 1 },
                                        }}
                                    >
                                        <img
                                            src={`${url}/${photo}`}
                                            alt={`proof-${i}`}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                        <Box className="overlay" sx={{
                                            position: 'absolute', inset: 0,
                                            background: 'rgba(0,0,0,0.35)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            opacity: 0, transition: 'opacity 0.2s',
                                        }}>
                                            <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>
                                                {tLocal.viewPhoto}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography sx={{ color: '#94A3B8', fontSize: '13px', fontStyle: 'italic' }}>
                            {tLocal.noPhotos}
                        </Typography>
                    )}
                </Box>

                {/* Optional fields — GPS + Video + Notes */}
                {(proof.gpsLocation || proof.videoUrl || proof.additionalNotes) && (
                    <Box sx={{ px: '28px', pt: '22px', pb: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        {proof.gpsLocation && (
                            <Box>
                                <SectionLabel icon={<IconMapPin />} label={tLocal.gps} />
                                <Typography sx={{
                                    fontSize: '13px', fontWeight: 500,
                                    color: colors.carbon_text_main || '#0F1729',
                                    background: '#F8FAFC', borderRadius: '10px',
                                    px: '14px', py: '10px',
                                    border: '1px solid #E2E8F0',
                                    fontFamily: 'monospace',
                                }}>
                                    {proof.gpsLocation}
                                </Typography>
                            </Box>
                        )}

                        {proof.videoUrl && (
                            <Box>
                                <SectionLabel icon={<IconVideo />} label={tLocal.videoUrl} />
                                <Typography
                                    component="a"
                                    href={proof.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        fontSize: '13px', color: colors.carbon_bg_main || '#00B4D8',
                                        fontWeight: 500, wordBreak: 'break-all',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {proof.videoUrl}
                                </Typography>
                            </Box>
                        )}

                        {proof.additionalNotes && (
                            <Box>
                                <SectionLabel icon={<IconNotes />} label={tLocal.notes} />
                                <Typography sx={{
                                    fontSize: '14px', color: '#475569', fontWeight: 400, lineHeight: '22px',
                                    background: '#F8FAFC', borderRadius: '12px',
                                    px: '16px', py: '14px',
                                    border: '1px solid #E2E8F0',
                                }}>
                                    {proof.additionalNotes}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Bottom padding when no optional fields */}
                {!(proof.gpsLocation || proof.videoUrl || proof.additionalNotes) && (
                    <Box sx={{ height: '28px' }} />
                )}

            </DialogContent>
        </Dialog>
    );
};

// ===== MAIN COMPONENT =====
const CarbonCreditsMyProofs = ({ userData, onBack, onUploadProof }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language || 'en';
    const tLocal = t[lang] || t['en'];
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [proofs, setProofs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProof, setSelectedProof] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalProofs, setTotalProofs] = useState(0);

    // Image Viewer States
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImage, setViewerImage] = useState('');
    const [zoom, setZoom] = useState(1);

    const handleImageClick = (imageUrl) => {
        setViewerImage(imageUrl);
        setViewerOpen(true);
        setZoom(1);
    };

    useEffect(() => {
        if (!userData?._id) return;
        setLoading(true);
        
        let fetchUrl = projectId 
            ? `${url}/api/carbon-credits/proofs/project/${projectId}`
            : `${url}/api/carbon-credits/proofs/user/${userData._id}`;
        
        fetchUrl += `?page=${page}`;

        fetch(fetchUrl)
            .then(res => res.json())
            .then(data => {
                setProofs(data.proofs || []);
                setPageCount(data.pageCount || 1);
                setTotalProofs(data.totalProofs || 0);
            })
            .catch(err => console.error('Error fetching proofs:', err))
            .finally(() => setLoading(false));
    }, [userData, projectId, page]);

    const handleBack = () => {
        if (onBack) onBack();
        else navigate('/carbon-credits');
    };

    const handleUpload = () => {
        if (onUploadProof) onUploadProof();
        else navigate('/carbon-credits/upload-proof');
    };

    const handleCardClick = (proof) => {
        setSelectedProof(proof);
        setModalOpen(true);
    };

    // Group by status for summary pills
    const pending  = proofs.filter(p => p.status === 'Pending').length;
    const approved = proofs.filter(p => p.status === 'Approved').length;
    const rejected = proofs.filter(p => p.status === 'Rejected').length;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#FAFAFA', pb: 10 }}>
            <Box sx={{ maxWidth: '720px', mx: 'auto', px: { xs: '16px', md: 0 }, pt: { xs: 2, md: 4 } }}>

                {/* Back button */}
                <Button
                    onClick={handleBack}
                    variant="outlined"
                    startIcon={<IconArrowLeft size={18} stroke={2.5} />}
                    sx={{
                        borderRadius: '50px',
                        px: 3, height: '48px',
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 700, fontSize: '14px', textTransform: 'none',
                        color: colors.carbon_text_main || '#1C252E',
                        borderColor: '#E0E5EB', mb: '24px', minWidth: '110px',
                        '&:hover': { borderColor: '#919EAB', backgroundColor: '#F4F6F8' },
                    }}
                >
                    {tLocal.back}
                </Button>

                {/* Title */}
                <Box sx={{ mb: '24px' }}>
                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 700, fontSize: '24px', letterSpacing: '-0.6px',
                        color: colors.carbon_text_main || '#0F1729', mb: '4px',
                    }}>
                        {tLocal.title}
                    </Typography>
                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontSize: '14px', color: '#65758B',
                    }}>
                        {tLocal.subtitle}
                    </Typography>
                </Box>

                {/* Summary pills */}
                {proofs.length > 0 && (
                    <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', mb: '24px' }}>
                        {[
                            { label: `${proofs.length} Total`, bg: '#F1F5F9', color: '#334155' },
                            { label: `${pending} Pending`,  bg: STATUS_META.Pending.bg,  color: STATUS_META.Pending.color },
                            { label: `${approved} Approved`, bg: STATUS_META.Approved.bg, color: STATUS_META.Approved.color },
                            { label: `${rejected} Rejected`, bg: STATUS_META.Rejected.bg, color: STATUS_META.Rejected.color },
                        ].map((pill) => (
                            <Box key={pill.label} sx={{
                                px: '14px', py: '6px', borderRadius: '20px',
                                background: pill.bg, color: pill.color,
                                fontSize: '13px', fontWeight: 700,
                                fontFamily: fonts.publicSans || fonts.poppins,
                            }}>
                                {pill.label}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Content */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress size={40} sx={{ color: colors.carbon_bg_main || '#00B4D8' }} />
                    </Box>
                ) : proofs.length === 0 ? (
                    // Empty state
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', py: 8, gap: '20px',
                        border: `2px dashed ${colors.carbon_border_dashed || '#E2E8F0'}`,
                        borderRadius: '20px', background: 'rgba(248,250,252,0.5)',
                    }}>
                        <Box sx={{
                            width: '72px', height: '72px', borderRadius: '50%',
                            background: 'rgba(0,180,216,0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: colors.carbon_bg_main || '#00B4D8',
                        }}>
                            <IconCamera size={36} stroke={1.5} />
                        </Box>
                        <Box sx={{ textAlign: 'center', px: '24px' }}>
                            <Typography sx={{
                                fontFamily: fonts.manrope || fonts.poppins,
                                fontWeight: 700, fontSize: '18px',
                                color: colors.carbon_text_main || '#0F1729', mb: '8px',
                            }}>
                                {tLocal.noProofs}
                            </Typography>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontSize: '14px', color: '#64748B', lineHeight: '22px',
                            }}>
                                {tLocal.noProofsHint}
                            </Typography>
                        </Box>
                        <Button
                            onClick={handleUpload}
                            variant="contained"
                            sx={{
                                background: colors.carbon_bg_main || '#00B4D8',
                                borderRadius: '50px', px: 4, height: '48px',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700, fontSize: '14px', textTransform: 'none',
                                boxShadow: '0px 4px 12px rgba(0,180,216,0.25)',
                                '&:hover': { background: '#009DC0', transform: 'scale(1.02)' },
                                transition: 'all 0.2s',
                            }}
                        >
                            {tLocal.uploadNow}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {proofs.map((proof) => (
                                <ProofCard key={proof._id} proof={proof} tLocal={tLocal} onClick={handleCardClick} />
                            ))}
                        </Box>
                        
                        {pageCount > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 4 }}>
                                <Pagination 
                                    count={pageCount}
                                    page={page}
                                    onChange={(e, v) => setPage(v)}
                                    color="primary"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontWeight: 700,
                                            borderRadius: '12px',
                                            padding: '20px',
                                            fontFamily: fonts.poppins,
                                            color: colors.carbon_text_main,
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}</Box>

            {/* Detail Modal */}
            <ProofDetailModal
                proof={selectedProof}
                open={modalOpen}
                onClose={() => { setModalOpen(false); setSelectedProof(null); }}
                tLocal={tLocal}
                onImageClick={handleImageClick}
            />

            {/* Image Viewer Lightbox */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="md"
                PaperProps={{
                    sx: { 
                        backgroundColor: 'rgba(15, 23, 24, 0.98)', 
                        backdropFilter: 'blur(12px)',
                        boxShadow: 'none',
                        borderRadius: '28px',
                        overflow: 'hidden',
                        m: 2,
                        height: '80vh',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
                    {/* Viewer Header */}
                    <Box sx={{ 
                        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                        p: 2, position: 'absolute', top: 0, left: 0, zIndex: 10 
                    }}>
                        <Box sx={{ display: 'flex', gap: 1, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '12px', p: 0.5 }}>
                            <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.5, 4))} sx={{ color: '#fff' }}>
                                <IconZoomIn size={22} />
                            </IconButton>
                            <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.5, 0.5))} sx={{ color: '#fff' }}>
                                <IconZoomOut size={22} />
                            </IconButton>
                            <IconButton onClick={() => setZoom(1)} sx={{ color: '#fff' }}>
                                <IconRefresh size={22} />
                            </IconButton>
                        </Box>
                        <IconButton onClick={() => setViewerOpen(false)} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}>
                            <IconX size={24} />
                        </IconButton>
                    </Box>
    
                    {/* Image Container */}
                    <Box sx={{ 
                        width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        overflow: 'auto', p: 4, mt: 6,
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}>
                        <img 
                            src={viewerImage} 
                            alt="Full proof" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                objectFit: 'contain',
                                transform: `scale(${zoom})`,
                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                borderRadius: '8px'
                            }} 
                        />
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default CarbonCreditsMyProofs;
